// Frontend error reporter.
//
// Mirrors the backend Sentry stub in deskte-api/src/index.ts: when the
// VITE_SENTRY_DSN build-time env var is set, install global window.onerror
// + unhandledrejection handlers that POST a small JSON envelope to
// Sentry's HTTP store endpoint. No @sentry/browser dep, no auto-
// instrumentation — just uncaught errors so Lissette can see SPA crashes
// in the same Sentry project as the API.
//
// Activation
//   - Set VITE_SENTRY_DSN in the SWA build env (azure-static-web-apps-*.yml)
//   - The DSN is baked into the bundle at build time. That's fine: a Sentry
//     "public key" is meant to be public — it scopes events to one project
//     and has no auth power.
//
// Privacy
//   - PHI never enters the SPA in a way that survives a render error path
//     (we strip server-side, components only see masked values), so the
//     payload here can include the stack + URL + user agent without
//     bleeding customer data.
//   - We DO NOT include localStorage, cookies, form values, or window
//     state. If a future code change starts surfacing PHI in error
//     messages, audit this file too.

const DSN = import.meta.env.VITE_SENTRY_DSN;
const ENV = import.meta.env.MODE || 'development';

let parsed = null;
function parseDsn() {
  if (parsed) return parsed;
  if (!DSN || typeof DSN !== 'string') return null;
  const m = /^https?:\/\/([^@]+)@([^/]+)\/(.+)$/.exec(DSN);
  if (!m) return null;
  const [, publicKey, host, projectId] = m;
  parsed = {
    url: `https://${host}/api/${projectId}/store/`,
    authHeader: `Sentry sentry_version=7, sentry_key=${publicKey}, sentry_client=deskte-spa/1.0`,
  };
  return parsed;
}

// Strip portal tokens from URLs before they leave the browser. The three
// public-portal routes (/upload/<token>, /cliente/<token>, /sign/<token>) use
// the token in the path as the only credential — leaking that into Sentry
// would let anyone with access to the project view the raw upload page for
// real clients. Replace the token segment with "<redacted>" while keeping the
// route prefix so we can still tell which portal threw.
function redactPortalTokens(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  return rawUrl.replace(/\/(upload|cliente|sign)\/[^/?#]+/gi, '/$1/<redacted>');
}

// Dedup. Browsers fire window.onerror multiple times for the same error in
// some scenarios (script errors triggered by an inline handler, then again
// from the global stack), and React's componentDidCatch / error boundaries
// re-emit similar messages. We hold each fingerprint for 5 minutes and
// silently drop repeats — Sentry's free tier is event-quota based and we
// don't want a single tight render loop to burn 5K events in a minute.
const seen = new Map();
const DEDUP_TTL_MS = 5 * 60 * 1000;
function isDuplicate(fp) {
  const now = Date.now();
  for (const [k, t] of seen) if (now - t > DEDUP_TTL_MS) seen.delete(k);
  if (seen.has(fp)) return true;
  seen.set(fp, now);
  return false;
}

function parseStack(stack) {
  if (!stack || typeof stack !== 'string') return [];
  const frames = [];
  for (const line of stack.split('\n').slice(1, 21)) {
    // V8/Chrome frames: "at fn (URL:line:col)" or anonymous "at URL:line:col".
    // We MUST keep the COLUMN: Sentry resolves source maps by line AND column,
    // so the old regex (which captured line but threw the column away) left
    // every frame un-symbolicatable even once maps are deployed — the report
    // could only ever read "Ce is not a function" instead of the real name.
    // abs_path mirrors filename so Sentry can fetch the .map for that exact
    // asset URL.
    const m = /at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/.exec(line) || /at\s+(.+?):(\d+):(\d+)/.exec(line);
    if (!m) continue;
    if (m.length === 5) {
      frames.push({ function: m[1], filename: m[2], abs_path: m[2], lineno: Number(m[3]), colno: Number(m[4]) });
    } else {
      frames.push({ function: '<anonymous>', filename: m[1], abs_path: m[1], lineno: Number(m[2]), colno: Number(m[3]) });
    }
  }
  return frames.reverse();
}

// --- A: drop events that are not actionable app bugs ----------------------
// Automated browsers (uptime monitors, crawlers, headless scrapers) run our
// JS from datacenter IPs and routinely trip errors on stale cached chunks or
// missing engine features — noise that buries the real reports (the 2026-06-23
// "Ce is not a function" event came from a Vultr datacenter IP, i.e. a bot).
// We also drop browser-extension crashes (the user's extension failing on our
// page, not our code) and the well-known opaque/benign messages every SPA gets.
const BOT_UA_RE = /(bot|crawl|spider|slurp|headless|phantom|puppeteer|playwright|selenium|webdriver|lighthouse|pingdom|uptimerobot|statuscake|datadog|newrelic|facebookexternalhit|bingpreview|ahrefs|semrush|petalbot|gptbot|claudebot)/i;
// Conservative: only the truly benign/opaque messages. We deliberately do NOT
// match things like "null is not an object" — those are real bugs.
const NOISE_RE = /(^script error|resizeobserver loop|non-error promise rejection)/i;
const EXT_URL_RE = /(chrome|moz|safari|safari-web)-extension:\/\//i;

function isAutomatedBrowser() {
  try {
    if (typeof navigator === 'undefined') return false;
    if (navigator.webdriver === true) return true; // Selenium/Playwright/Puppeteer/headless
    return BOT_UA_RE.test(navigator.userAgent || '');
  } catch (_) {
    return false;
  }
}

function isNoise(message, stack) {
  const m = (message || '').trim();
  if (!m) return true;                  // empty/opaque — nothing to act on
  if (NOISE_RE.test(m)) return true;
  // Extension-only crash: every URL-bearing frame points at a browser
  // extension and none at our own bundle → it is the extension's bug.
  const urls = (stack || '').match(/\b(?:https?|chrome-extension|moz-extension|safari-extension|safari-web-extension):\/\/[^\s):]+/gi);
  if (urls && urls.length) {
    const ext = urls.filter((u) => EXT_URL_RE.test(u)).length;
    if (ext > 0 && urls.length - ext === 0) return true;
  }
  return false;
}

async function postEvent(payload) {
  const cfg = parseDsn();
  if (!cfg) return;
  try {
    await fetch(cfg.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Sentry-Auth': cfg.authHeader },
      body: JSON.stringify(payload),
      // No keepalive: a slow Sentry shouldn't tie up the browser tab.
      // If we're unmounting we just drop the event.
      keepalive: true,
    });
  } catch (_) {
    // Network error / DNS failure / Sentry quota hit — swallow. The
    // browser console already has the original error so we haven't
    // hidden anything.
  }
}

function reportError(err, source) {
  if (!parseDsn() || ENV === 'development') return;
  if (isAutomatedBrowser()) return;               // A: skip bots / monitors / headless
  const message = (err && err.message) || String(err);
  const stack = (err && err.stack) || '';
  if (isNoise(message, stack)) return;            // A: skip benign / extension noise
  const fingerprint = `${source}|${message}|${stack.slice(0, 80)}`;
  if (isDuplicate(fingerprint)) return;
  postEvent({
    event_id: Math.random().toString(16).slice(2).padEnd(32, '0'),
    timestamp: new Date().toISOString(),
    level: 'error',
    platform: 'javascript',
    environment: ENV,
    release: import.meta.env.VITE_GIT_SHA || undefined,
    server_name: 'deskte-spa',
    exception: {
      values: [{
        type: (err && err.name) || 'Error',
        value: message,
        stacktrace: stack ? { frames: parseStack(stack) } : undefined,
      }],
    },
    extra: {
      source,
      url: typeof window !== 'undefined' ? redactPortalTokens(window.location.href) : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    },
  });
}

export function installFrontendSentry() {
  if (typeof window === 'undefined') return;
  if (!parseDsn()) {
    // DSN absent — install nothing. Browser dev console keeps original
    // errors and React error boundaries still render fallbacks.
    return;
  }
  window.addEventListener('error', (event) => {
    reportError(event.error || event.message, 'window.error');
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    reportError(reason, 'unhandledrejection');
  });
}

// Public helper for code paths that already catch the error but want to
// forward it (e.g. an apiFetch failure that the UI handled but is worth
// recording). Keeps the dedup + sampling logic in one place.
export function reportFrontendError(err, source = 'manual') {
  reportError(err, source);
}
